// ============================================================================
// 진단 표시 (D) — 손가락 점 + 복원된 샘플 궤적, 저항/힘/속도 막대, 붙음/미끄러짐 상태, 프레임 시간.
// ============================================================================
import { Sim } from './sim';
import { PointerInput } from './input';
import { audio } from './audio';
import { haptics } from './haptics';
import { Renderer } from './render';
import { P, WORLD, CARD_DRAW, getDrawSeed } from './params';
import { BinderSim } from './binder-sim';
import { BinderView } from './binder-render';

/** 축 벡터를 사람이 읽는 방향 이름으로 */
function dirName(x: number, y: number): string {
  if (Math.abs(x) < 1e-6 && Math.abs(y) < 1e-6) return '-';
  const a = (Math.atan2(y, x) * 180) / Math.PI;
  const names = ['→', '↗', '↑', '↖', '←', '↙', '↓', '↘'];
  return names[Math.round(((a + 360) % 360) / 45) % 8];
}

export class Diag {
  visible = false;
  private el: HTMLElement;
  private cv: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private frameHist = new Float32Array(120);
  private fi = 0;
  private histR = new Float32Array(240);
  private histF = new Float32Array(240);
  private histV = new Float32Array(240);
  private histS = new Uint8Array(240);
  private hi = 0;
  private lastEvent = '';
  private eventT = 0;
  private stepsThisFrame = 0;

  private binderSim: BinderSim | null = null;
  private binderView: BinderView | null = null;
  private getScreen: () => string = () => 'open';

  constructor(private sim: Sim, private input: PointerInput, private renderer: Renderer, overlay: HTMLCanvasElement) {
    this.el = document.getElementById('diag')!;
    this.cv = overlay;
    this.ctx = overlay.getContext('2d')!;
    // 이벤트는 main.ts 가 두 sim 에서 모아 noteEvent 로 넘긴다 (여기서 onEvent 를 잡으면 덮어써진다)
    this.resize();
    window.addEventListener('resize', () => this.resize());
  }

  /** main.ts 가 open/binder 양쪽 이벤트를 여기로 흘린다. */
  noteEvent(n: string): void {
    this.lastEvent = n;
    this.eventT = performance.now();
  }

  attachBinder(b: BinderSim, getScreen: () => string, view: BinderView): void {
    this.binderSim = b;
    this.getScreen = getScreen;
    this.binderView = view;
  }

  resize(): void {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.cv.width = Math.floor(window.innerWidth * dpr);
    this.cv.height = Math.floor(window.innerHeight * dpr);
    this.cv.style.width = window.innerWidth + 'px';
    this.cv.style.height = window.innerHeight + 'px';
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  toggle(force?: boolean): void {
    this.visible = force ?? !this.visible;
    this.el.style.display = this.visible ? 'block' : 'none';
    if (!this.visible) this.ctx.clearRect(0, 0, this.cv.width, this.cv.height);
  }

  /** 물리 스텝마다 호출 (히스토리) */
  recordStep(): void {
    const s = this.sim;
    let R = 0, F = 0, V = 0, st = 0;
    if (s.phase === 'cutting') {
      R = s.cut.Rk;
      F = s.cut.F;
      V = s.cut.vb;
      st = s.cut.moving ? 2 : s.cut.active ? 1 : 0;
    } else if (s.phase === 'reveal') {
      R = s.sw.state === 'slip' ? s.sw.Rk : s.sw.Rs;
      F = s.sw.F;
      V = Math.hypot(s.sw.vx, s.sw.vy);
      st = s.sw.state === 'slip' ? 2 : s.sw.state === 'stuck' ? 1 : s.sw.state === 'return' ? 3 : 0;
    }
    this.histR[this.hi] = R;
    this.histF[this.hi] = F;
    this.histV[this.hi] = V;
    this.histS[this.hi] = st;
    this.hi = (this.hi + 1) % this.histR.length;
    this.stepsThisFrame++;
  }

  /** 렌더 프레임마다 호출 */
  frame(dtMs: number): void {
    this.frameHist[this.fi] = dtMs;
    this.fi = (this.fi + 1) % this.frameHist.length;
    if (!this.visible) {
      this.stepsThisFrame = 0;
      return;
    }
    this.drawOverlay();
    this.writeText(dtMs);
    this.stepsThisFrame = 0;
  }

  private drawOverlay(): void {
    const ctx = this.ctx;
    const W = window.innerWidth, H = window.innerHeight;
    ctx.clearRect(0, 0, W, H);
    const inp = this.input;
    // 샘플 궤적 (복원된 좌표는 주황, 원본 이벤트는 흰색)
    for (const s of inp.history) {
      const p = this.renderer.toScreen(s.x, s.y);
      ctx.fillStyle = s.coalesced ? 'rgba(255,160,40,0.9)' : 'rgba(255,255,255,0.9)';
      ctx.fillRect(p.x - 1.5, p.y - 1.5, 3, 3);
    }
    // 현재 손가락 점 (이벤트 시각 rawPos = 1프레임 응답)
    if (inp.down) {
      const p = this.renderer.toScreen(inp.rawPos.x, inp.rawPos.y);
      ctx.strokeStyle = '#00ff88';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(p.x, p.y, 14, 0, Math.PI * 2);
      ctx.stroke();
      const q = this.renderer.toScreen(inp.pos.x, inp.pos.y);
      ctx.fillStyle = '#00ff88';
      ctx.beginPath();
      ctx.arc(q.x, q.y, 4, 0, Math.PI * 2);
      ctx.fill();
    }
    // 절취선 가이드 (그래픽 끈 상태에서 위치 참고)
    if (!this.renderer.graphicsOn) {
      const a = this.renderer.toScreen(-WORLD.packW / 2, this.sim.perfY);
      const b = this.renderer.toScreen(WORLD.packW / 2, this.sim.perfY);
      ctx.strokeStyle = 'rgba(255,255,255,0.25)';
      ctx.setLineDash([6, 6]);
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.stroke();
      ctx.setLineDash([]);
    }
    // 막대 + 스트립 차트 (우상단)
    const gx = W - 210, gy = 10, gw = 200, gh = 90;
    ctx.fillStyle = 'rgba(0,0,0,0.45)';
    ctx.fillRect(gx, gy, gw, gh + 60);
    const n = this.histR.length;
    const maxR = 6, maxV = 20;
    const drawSeries = (arr: Float32Array, max: number, color: string) => {
      ctx.strokeStyle = color;
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (let i = 0; i < n; i++) {
        const v = arr[(this.hi + i) % n];
        const x = gx + (i / n) * gw;
        const y = gy + gh - Math.min(1, v / max) * gh;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();
    };
    // 상태 띠
    for (let i = 0; i < n; i++) {
      const st = this.histS[(this.hi + i) % n];
      if (!st) continue;
      ctx.fillStyle = st === 2 ? 'rgba(0,255,136,0.35)' : st === 1 ? 'rgba(255,80,80,0.35)' : 'rgba(120,120,255,0.35)';
      ctx.fillRect(gx + (i / n) * gw, gy + gh - 6, gw / n + 0.5, 6);
    }
    drawSeries(this.histR, maxR, '#ff5050');
    drawSeries(this.histF, maxR, '#ffd040');
    drawSeries(this.histV, maxV, '#40d0ff');
    // 현재 막대
    const s = this.sim;
    const bars: [string, number, number, string][] =
      s.phase === 'cutting'
        ? [
            ['Rk', s.cut.Rk, maxR, '#ff5050'],
            ['Rs', s.cut.Rs, maxR, '#a03030'],
            ['F', s.cut.F, maxR, '#ffd040'],
            ['vb', s.cut.vb, maxV, '#40d0ff'],
          ]
        : [
            ['Rk', s.sw.Rk, maxR, '#ff5050'],
            ['Rs', s.sw.Rs, maxR, '#a03030'],
            ['F', s.sw.F, maxR, '#ffd040'],
            ['v', Math.hypot(s.sw.vx, s.sw.vy), maxV, '#40d0ff'],
          ];
    bars.forEach(([name, v, max, col], i) => {
      const bx = gx + 6 + i * 48, by = gy + gh + 8;
      ctx.fillStyle = 'rgba(255,255,255,0.15)';
      ctx.fillRect(bx, by, 40, 40);
      ctx.fillStyle = col;
      const hh = Math.min(1, v / max) * 40;
      ctx.fillRect(bx, by + 40 - hh, 40, hh);
      ctx.fillStyle = '#fff';
      ctx.font = '10px monospace';
      ctx.fillText(`${name} ${v.toFixed(2)}`, bx, by + 52);
    });
    // 프레임 시간 히스토그램 (우하단)
    const fx = W - 210, fy = H - 60, fw = 200, fh = 40;
    ctx.fillStyle = 'rgba(0,0,0,0.45)';
    ctx.fillRect(fx, fy, fw, fh);
    ctx.fillStyle = '#9f9';
    const m = this.frameHist.length;
    for (let i = 0; i < m; i++) {
      const v = this.frameHist[(this.fi + i) % m];
      const hh = Math.min(1, v / 50) * fh;
      ctx.fillStyle = v > 20 ? '#f66' : '#9f9';
      ctx.fillRect(fx + (i / m) * fw, fy + fh - hh, fw / m, hh);
    }
    ctx.strokeStyle = 'rgba(255,255,255,0.3)';
    ctx.beginPath();
    ctx.moveTo(fx, fy + fh - (16.7 / 50) * fh);
    ctx.lineTo(fx + fw, fy + fh - (16.7 / 50) * fh);
    ctx.stroke();
  }

  private writeText(dtMs: number): void {
    const s = this.sim, inp = this.input;
    let avg = 0, max = 0;
    for (const v of this.frameHist) {
      avg += v;
      if (v > max) max = v;
    }
    avg /= this.frameHist.length;
    const f = (v: number, d = 2) => (Number.isFinite(v) ? v.toFixed(d) : '-');
    const lines = [
      `screen ${this.getScreen()}   phase ${s.phase}   t ${f(s.time, 1)}s   evt ${this.lastEvent}${performance.now() - this.eventT < 600 ? ' *' : ''}`,
      `frame ${f(dtMs, 1)}ms  avg ${f(avg, 1)}  max ${f(max, 1)}   steps/frame ${this.stepsThisFrame}`,
      `input down=${inp.down ? 1 : 0}  samples/frame ${inp.statSamplesThisFrame} (coalesced ${inp.statCoalescedThisFrame}, events ${inp.statEventsThisFrame})  /s ${inp.statSamplesPerSec}  pending ${inp.pending}`,
      `vFast (${f(inp.vFast.x, 1)}, ${f(inp.vFast.y, 1)})  vSlow (${f(inp.vSlow.x, 1)}, ${f(inp.vSlow.y, 1)})  release (${f(inp.releaseVel.x, 1)}, ${f(inp.releaseVel.y, 1)})`,
      `audio ${audio.ready ? 'ready' : 'idle'}  latency ${f(audio.latencyMs, 1)}ms  cutLvl ${f(audio.lastCutLevel)}  fricLvl ${f(audio.lastFrictionLevel)}  crackle ${f(audio.crackleRate, 0)}/s`,
      `haptic ${haptics.enabled ? 'on' : 'OFF'} ${haptics.status()}`,
    ];
    if (s.phase === 'cutting' || s.phase === 'pack') {
      const c = s.cut;
      lines.push(
        `cut dir ${c.dir}  front ${f(c.front)}  lag ${f(c.lag, 3)}  F ${f(c.F)}  Rk ${f(c.Rk)}  Rs ${f(c.Rs)}  vb ${f(c.vb)}  ${c.active ? (c.moving ? 'CUTTING' : 'STUCK') : 'idle'}`,
      );
    }
    if (s.phase === 'flipping') lines.push(`flip θ ${f(s.flip.theta)}  ω ${f(s.flip.omega)}  tilt ${f(s.flip.tilt, 3)}`);
    if (s.phase === 'reveal' || s.phase === 'done') {
      const w = s.sw;
      lines.push(
        `swipe ${w.state.toUpperCase()}  top ${w.top}  pos (${f(w.px)}, ${f(w.py)})  bend ${f(w.bend, 3)}/${f(w.bMax, 3)}  F ${f(w.F)}  |v| ${f(Math.hypot(w.vx, w.vy))}`,
        `  lag dir ${dirName(w.bdx, w.bdy)}  contact ${f(w.contactProj)}  slips ${w.slipCount}  flyers ${w.flyers.length}`,
        `  reveal ${(w.reveal * 100).toFixed(0)}% of card#${w.revealIdx}(rank ${w.revealIdx >= 0 && w.revealIdx < CARD_DRAW.ranks.length ? CARD_DRAW.ranks[w.revealIdx] : "-"})  레어 ${w.rareActive ? "ON" : "--"} t ${f(w.rareT)} pulses ${w.rarePulses}`,
      );
      // AR·UR 등장 연출. 빛도 소리도 화면 밖에서는 못 보므로 숫자로 남긴다.
      const fx = s.fx;
      if (fx.idx >= 0) {
        lines.push(`  등장연출 card#${fx.idx} rank ${fx.rank} ×${f(fx.scale, 2)}  t ${f(fx.t, 2)}s  band ${f(s.rs.fxBand, 2)} 세기 ${f(s.rs.fxStrength, 2)} flash ${f(s.rs.fxFlash, 2)}`);
      }
    }
    const b = this.binderSim;
    if (b && this.getScreen() === 'binder') {
      const kinds = ['none', 'flip', 'pan', 'edge'];
      const cache = this.binderView ? this.binderView.cardCache.residentCells() : [];
      const dcache = this.binderView ? this.binderView.cardCache.residentDetails() : [];
      lines.push(
        `binder ${b.mode.toUpperCase()}  면 ${b.face}/${b.faceCountNow}  seg ${kinds[b.seg.kind]}${b.seg.dir > 0 ? '+' : '-'} ${b.seg.from}→${b.seg.to}  u ${f(b.seg.u, 3)}  v ${f(b.seg.v, 2)}  τ ${f(b.seg.tau, 2)}${b.seg.peeled ? ' peeled' : ''}`,
        `  viewX ${f(b.viewX, 3)}  θ ${f(b.theta, 3)}  W ${f(b.W, 2)}  칸그림 ${cache.length}/${P.binder.cellTexMax}  page ${f(audio.lastPageLevel)}`,
      );
      if (b.mode === 'detail') {
        lines.push(
          `  상세 ${b.det.phase} slot ${b.det.slot} cell ${b.det.cell} ${b.det.owned ? '보유' : '미보유'}  tex ${this.binderView?.detailSourceA}  원본캐시 [${dcache.join(',')}]  dim ${f(b.det.dim)}`,
        );
      }
      if (b.mode === 'stow')
        lines.push(
          `  수납 ${b.stow.phase} ${b.stow.cur + 1}/${b.stow.n}${b.seg.auto ? ' auto' : ''}  목표면 ${b.stow.face[b.stow.cur] ?? '-'}  t ${f(b.stow.t, 2)}s (단계 ${f(b.stow.ct, 2)}s)  착지 ${[...b.stow.landed].join('')}`,
        );
    }
    const seed = getDrawSeed();
    lines.push(`draw ${CARD_DRAW.kinds.map((k) => k.replace('card_', '')).join(' ')}  seed ${seed === null ? '없음(리셋마다 새로)' : seed}`);
    this.el.textContent = lines.join('\n');
  }
}
